import { useRouter } from "expo-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import { KeyboardAvoidingView, Platform, ScrollView, StyleSheet } from "react-native";
import {
  AddressForm,
  HomeTypeQuestion,
  HouseAreaOutOfScopeSheet,
  LocationDisclaimerSheet,
  PresenceInfoQuestion,
  ServiceSceneDescription,
  ServiceSceneHeader,
  loaderRef,
} from "../../../src/components";
import { useAppSelector, useCreateOrUpdateAddress } from "../../../src/hooks";
import type { ReservationFormParams } from "../../../src/store/draftReservation/types";

import {
  extrudeAdditionalInfo,
  useV2GetCheckoutQuery,
  useValidateAddressInLocalizationMutation,
} from "@stepapp/core";
import { CategoryType } from "@stepapp/core/apiTypes";
import { Block, Button, Divider, palette, spacing } from "@stepapp/ui";
import { useTranslation } from "react-i18next";

const ServiceAddAddressScreen = () => {
  const { t } = useTranslation();
  const { back } = useRouter();
  const properties = useAppSelector(({ user }) => user.properties);
  const isUserLogged = useAppSelector(({ user }) => !!user.id);
  const localizationId = useAppSelector(({ appConfig }) => appConfig.localizationId);
  const [notAvailableLocation, setNotAvailableLocation] = useState(false);
  const { data } = useV2GetCheckoutQuery();

  const [validateAddress, { loading: validateAddressLoading }] =
    useValidateAddressInLocalizationMutation();

  const {
    handleSubmit,
    setValue,
    watch,
    trigger,
    formState: { errors },
  } = useFormContext<ReservationFormParams>();
  const [address, propertyId, meta] = watch(["address", "propertyId", "meta"]);

  const filteredProperties = useMemo(
    () => properties.filter((item) => item?.localizationId === localizationId),
    [properties, localizationId],
  );

  const hasProperties = useMemo(
    () => !!filteredProperties?.[0]?.propertyId,
    [filteredProperties?.[0]?.propertyId],
  );

  const property = useMemo(
    () => properties?.find((property) => property?.propertyId === propertyId),
    [properties, propertyId],
  );
  const requirePresenceInfo = CategoryType.Massage !== data?.v2GetCheckout?.bundle?.categoryType;

  const [createOrUpdateAddress, { loading: addressLoading }] = useCreateOrUpdateAddress({
    property,
    isUserLogged,
    localizationId: property?.localizationId || localizationId,
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: skip
  useEffect(() => {
    const presenceNote = meta?.willCustomerBePresent
      ? `${t("pages.serviceDateAndAddress.iWillBeThere")}`
      : meta?.keyInfo || "";

    setValue("address.notes", `${extrudeAdditionalInfo(address?.notes) || ""}\n\n${presenceNote}`);
  }, [meta?.willCustomerBePresent, meta?.keyInfo, setValue, t]);

  const onSubmit = useCallback(
    async ({ meta, address }: ReservationFormParams) => {
      loaderRef.current?.showLoader();
      const additionalInfo = extrudeAdditionalInfo(address?.notes) || "";
      const info = meta || {};
      const descriptionAnnotation = info.willCustomerBePresent
        ? `${t("pages.serviceDateAndAddress.iWillBeThere")}`
        : info.keyInfo || "";

      const newValues = {
        ...address,
        floorArea: address?.floorArea || 0,
        notes: `${additionalInfo}\n\n${descriptionAnnotation}`,
      };

      const { data } = await validateAddress({
        variables: {
          payload: {
            city: address?.city || "",
            localizationId,
            postalOrZipCode: address?.postalOrZipCode || "",
            streetAddress1: address?.streetAddress1 || "",
            streetAddress2: address?.streetAddress2,
          },
        },
      });

      if (!data?.validateAddressInLocalization) {
        loaderRef.current?.hideLoader();
        return setNotAvailableLocation(true);
      }

      const isSuccess = await createOrUpdateAddress(newValues);

      isSuccess && setValue("propertyId", isSuccess?.propertyId);

      loaderRef.current?.hideLoader();
      back();
    },
    [createOrUpdateAddress, validateAddress, localizationId, back, setValue, t],
  );

  return (
    <>
      <KeyboardAvoidingView
        enabled
        behavior={Platform.select({ default: "padding", android: undefined })}
        keyboardVerticalOffset={spacing.unit * 20}
      >
        <ScrollView contentContainerStyle={styles.content}>
          <Block marginTop={1} marginBottom={3}>
            <ServiceSceneHeader name={t("pages.serviceDateAndAddress.addAddress")} />
            <ServiceSceneDescription
              description={t("pages.serviceDateAndAddress.addAddressDescription", {
                context: data?.v2GetCheckout?.bundle?.categoryType?.toLowerCase(),
              })}
            />
          </Block>

          <AddressForm
            city={address?.city || ""}
            doorCode={address?.doorCode || ""}
            errors={errors}
            floorArea={address?.floorArea || 0}
            notes={extrudeAdditionalInfo(address?.notes) || ""}
            postalOrZipCode={address?.postalOrZipCode || ""}
            setValue={setValue}
            trigger={trigger}
            streetAddress1={address?.streetAddress1 || ""}
            streetAddress2={address?.streetAddress2 || ""}
          />

          {requirePresenceInfo && (
            <>
              <Divider />

              <HomeTypeQuestion
                setValue={setValue}
                errorMsg={errors?.meta?.homeTypePlaceholder?.message}
                homeType={meta?.homeTypePlaceholder || ""}
              />

              <PresenceInfoQuestion
                errors={errors}
                keyInfo={meta?.keyInfo}
                setValue={setValue}
                willCustomerBePresent={meta?.willCustomerBePresent}
              />
            </>
          )}
        </ScrollView>
        {hasProperties && (
          <Block justifyCenter paddingVertical={2} style={styles.submitButton}>
            <Button
              disabled={addressLoading || validateAddressLoading}
              label={t("common.continue")}
              loading={addressLoading || validateAddressLoading}
              onPress={handleSubmit(onSubmit)}
              type="primary"
            />
          </Block>
        )}
      </KeyboardAvoidingView>

      <LocationDisclaimerSheet
        isVisible={notAvailableLocation}
        setModalVisible={setNotAvailableLocation}
      />

      <HouseAreaOutOfScopeSheet formError={errors?.address?.floorArea} />
    </>
  );
};

export default ServiceAddAddressScreen;

const styles = StyleSheet.create({
  content: {
    paddingBottom: spacing.unit * 12,
  },
  submitButton: {
    borderTopColor: palette.common.lightGray,
    borderTopWidth: spacing.border,
    backgroundColor: palette.common.white,
    width: "100%",
    position: "absolute",
    bottom: 0,
  },
});
